/**
 * Authenticator allowlist feature — queries + mutations.
 *
 * Reached via the cross-tenant platform mount at
 * `/api/v1/tenants/{tenant_id}/authenticator-allowlist`.
 * The admin console runs at admin.{base_domain}, so calls use
 * the platform path-scoped variant rather than the tenant-host
 * variant at /api/tenant/v1/... which would require a host
 * swap.
 *
 * Create and Delete are gated on auth_methods.manage at the
 * middleware layer; both wrap in withStepUp because the
 * permission is marked step-up-required.
 *
 * THERE IS NO UPDATE MUTATION, AND ITS ABSENCE IS THE API'S, NOT AN
 * OVERSIGHT HERE. platform_handler.go mounts exactly GET, POST,
 * GET {id}, DELETE {id}, seed-wellknown and seed-hardware-nfc — no
 * PATCH, no PUT. Every field on a row, including the
 * roaming_logon_allowed grant, is therefore settable only at CREATE.
 *
 * Do not add a useUpdateAllowlistEntryMutation to make an edit
 * affordance possible on the list: there is no route behind it, and
 * a toggle that 405s is worse than no toggle. Adding the route is an
 * API change with its own review — the grant widens what a carried
 * security key may do with an Active Directory certificate, so
 * making it editable in place is a decision about audit trail and
 * step-up scope, not a missing verb.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  allowlistEntrySchema,
  listAllowlistResponseSchema,
  type AllowlistEntry,
  type CreateAllowlistEntryInput,
  type ListAllowlistResponse,
} from "./schemas";

function base(tenantId: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantId)}/authenticator-allowlist`;
}

export async function fetchAllowlist(
  tenantId: string,
): Promise<ListAllowlistResponse> {
  const raw = await apiFetch<unknown>(base(tenantId));
  return listAllowlistResponseSchema.parse(raw);
}

export function useAllowlistQuery(tenantId: string) {
  return useQuery<ListAllowlistResponse, ApiError>({
    queryKey: queryKeys.authenticatorAllowlist.list(tenantId),
    queryFn: () => fetchAllowlist(tenantId),
    enabled: tenantId.length > 0,
  });
}

/**
 * Create one allowlist entry.
 *
 * The input is sent as the body verbatim, so CreateAllowlistEntryInput
 * IS the wire shape — its keys are snake_case for that reason, and
 * roaming_logon_allowed rides along without a mapping step. The
 * backend treats the field as optional-defaulting-false, so an older
 * console that omits it still creates a row with the grant off; this
 * one always sends an explicit boolean.
 */
export function useCreateAllowlistEntryMutation(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<AllowlistEntry, ApiError, CreateAllowlistEntryInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantId), {
          method: "POST",
          body: input,
        }).then((raw) => allowlistEntrySchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.authenticatorAllowlist.all(tenantId),
      });
    },
  });
}

export function useDeleteAllowlistEntryMutation(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (entryId) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantId)}/${encodeURIComponent(entryId)}`,
          { method: "DELETE" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.authenticatorAllowlist.all(tenantId),
      });
    },
  });
}

export interface SeedHardwareNFCResponse {
  readonly inserted: number;
  readonly catalogue_size: number;
}

/**
 * "Healthcare hardware-NFC default" one-click: primes the
 * tenant's allowlist with the curated set of hardware FIDO2-NFC
 * security keys (YubiKey 5 NFC, Feitian K9, Token2 PIN+, etc.)
 * that the always-armed Badge Sign-In tap ceremony will accept.
 *
 * Idempotent — duplicate AAGUIDs are silently skipped, so the
 * operator can re-run after the curated set grows. Additive
 * only — does NOT remove synced-passkey entries from a prior
 * SeedWellKnown run; the operator removes those via separate
 * Delete calls (or a future "remove synced passkeys" sweep).
 *
 * Step-up gated, same as Create/Delete.
 */
export function useSeedHardwareNFCMutation(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<SeedHardwareNFCResponse, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<SeedHardwareNFCResponse>(
          `${base(tenantId)}/seed-hardware-nfc`,
          { method: "POST" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.authenticatorAllowlist.all(tenantId),
      });
    },
  });
}
